import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calendar, RefreshCw, ChevronLeft, ChevronRight, CheckCircle, AlertTriangle, X, Sparkles } from 'lucide-react';
import { holidaysService, PublicHoliday, CreateHolidayDto } from '../../services/holidaysService';

const SOURCE_LABELS = {
  API: 'API',
  MANUAL: 'Manuel',
  TEMPLATE: 'Maroc',
};

const SOURCE_COLORS = {
  API: 'bg-purple-100 text-purple-700',
  MANUAL: 'bg-orange-100 text-orange-700',
  TEMPLATE: 'bg-emerald-100 text-emerald-700',
};

// Confirmation Modal Component
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  icon?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'primary',
  icon,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const buttonStyles = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    primary: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
  };

  const iconBgStyles = {
    danger: 'bg-red-100',
    primary: 'bg-primary-100',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6">
          <div className="flex items-start gap-4">
            {icon && (
              <div className={`p-3 rounded-full ${iconBgStyles[variant]} flex-shrink-0`}>
                {icon}
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center gap-2 ${buttonStyles[variant]}`}
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-300 ${
      type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
    } text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md`}>
      {type === 'success' ? (
        <CheckCircle size={20} />
      ) : (
        <AlertTriangle size={20} />
      )}
      <span className="flex-1 text-sm">{message}</span>
      <button onClick={onClose} className="p-1 hover:bg-white/20 rounded transition-colors">
        <X size={16} />
      </button>
    </div>
  );
};

interface HolidaysManagerProps {
  canManage?: boolean;
}

export const HolidaysManager: React.FC<HolidaysManagerProps> = ({ canManage = true }) => {
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<PublicHoliday | null>(null);
  const [formData, setFormData] = useState<CreateHolidayDto>({
    year: selectedYear,
    name: '',
    startDate: '',
    endDate: '',
  });

  // Modal states
  const [showInitModal, setShowInitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<PublicHoliday | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadHolidays();
  }, [selectedYear]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const data = await holidaysService.getHolidays(selectedYear);
      setHolidays(data);
    } catch (error) {
      console.error('Failed to load holidays:', error);
      showToast('Erreur lors du chargement des jours fériés', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      setInitializing(true);
      const result = await holidaysService.initializeHolidays(selectedYear);
      await loadHolidays();
      setShowInitModal(false);
      showToast(`${result.count} jours fériés initialisés pour ${selectedYear}`, 'success');
    } catch (error) {
      console.error('Failed to initialize holidays:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      showToast(errorMessage, 'error');
    } finally {
      setInitializing(false);
    }
  };

  const handleEdit = (holiday: PublicHoliday) => {
    setEditingHoliday(holiday);
    setFormData({
      year: holiday.year,
      name: holiday.name,
      nameAr: holiday.nameAr,
      startDate: holiday.startDate.split('T')[0],
      endDate: holiday.endDate.split('T')[0],
    });
    setShowForm(true);
  };

  const handleDeleteClick = (holiday: PublicHoliday) => {
    setHolidayToDelete(holiday);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!holidayToDelete) return;

    try {
      setDeleting(true);
      await holidaysService.deleteHoliday(holidayToDelete.id);
      await loadHolidays();
      setShowDeleteModal(false);
      setHolidayToDelete(null);
      showToast('Jour férié supprimé avec succès', 'success');
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      showToast(errorMessage, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      if (editingHoliday) {
        // `year`/`campusId` are immutable on a holiday and are rejected by the API
        // whitelist, so only send the editable fields on update.
        await holidaysService.updateHoliday(editingHoliday.id, {
          name: formData.name,
          nameAr: formData.nameAr,
          startDate: formData.startDate,
          endDate: formData.endDate,
        });
        showToast('Jour férié mis à jour avec succès', 'success');
      } else {
        await holidaysService.createHoliday(formData);
        showToast('Jour férié créé avec succès', 'success');
      }
      setShowForm(false);
      setEditingHoliday(null);
      setFormData({
        year: selectedYear,
        name: '',
        startDate: '',
        endDate: '',
      });
      await loadHolidays();
    } catch (error) {
      console.error('Failed to save holiday:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  const getDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return days === 1 ? '1 jour' : `${days} jours`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-gray-900">Jours fériés du Maroc</h3>
          <p className="text-sm text-gray-500">
            Les jours fériés nationaux sont initialisés automatiquement
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Year Navigation */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedYear(selectedYear - 1)}
              className="p-1.5 hover:bg-white rounded transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="font-semibold text-gray-900 px-2">{selectedYear}</span>
            <button
              onClick={() => setSelectedYear(selectedYear + 1)}
              className="p-1.5 hover:bg-white rounded transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {canManage && (
            <>
              <button
                onClick={() => setShowInitModal(true)}
                disabled={initializing}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RefreshCw size={18} className={initializing ? 'animate-spin' : ''} />
                Initialiser
              </button>

              <button
                onClick={() => {
                  setEditingHoliday(null);
                  setFormData({
                    year: selectedYear,
                    name: '',
                    startDate: '',
                    endDate: '',
                  });
                  setShowForm(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
                Ajouter
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      {holidays.length > 0 && (
        <div className="mb-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle size={16} />
            <span>{holidays.filter(h => h.source === 'TEMPLATE').length} jours fériés nationaux</span>
          </div>
          {holidays.filter(h => h.source === 'MANUAL').length > 0 && (
            <div className="flex items-center gap-2 text-orange-600">
              <span>{holidays.filter(h => h.source === 'MANUAL').length} ajoutés manuellement</span>
            </div>
          )}
        </div>
      )}

      {/* Holidays List */}
      <div className="space-y-3">
        {holidays.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="mx-auto mb-3 text-gray-400" size={48} />
            <p>Aucun jour férié configuré pour {selectedYear}</p>
            <p className="text-sm mt-2">Les jours fériés seront initialisés automatiquement au prochain démarrage.</p>
            <button
              onClick={() => setShowInitModal(true)}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Initialiser maintenant
            </button>
          </div>
        ) : (
          holidays.map((holiday) => (
            <div
              key={holiday.id}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              {/* Date indicator */}
              <div className="w-16 h-16 bg-amber-100 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-amber-700">
                  {new Date(holiday.startDate).getDate()}
                </span>
                <span className="text-xs text-amber-600 uppercase">
                  {new Date(holiday.startDate).toLocaleDateString('fr-FR', { month: 'short' })}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{holiday.name}</span>
                  {holiday.nameAr && (
                    <span className="text-sm text-gray-500" dir="rtl">
                      ({holiday.nameAr})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span>
                    {formatDate(holiday.startDate)}
                    {holiday.startDate !== holiday.endDate && ` - ${formatDate(holiday.endDate)}`}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    {getDuration(holiday.startDate, holiday.endDate)}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${SOURCE_COLORS[holiday.source]}`}>
                    {SOURCE_LABELS[holiday.source]}
                  </span>
                  {holiday.isExtended && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                      Prolongé
                    </span>
                  )}
                </div>
              </div>

              {/* Actions - only shown if user can manage */}
              {canManage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(holiday)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(holiday)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Initialize Confirmation Modal */}
      <ConfirmModal
        isOpen={showInitModal}
        title="Initialiser les jours fériés"
        message={`Voulez-vous initialiser les jours fériés marocains pour ${selectedYear} ? Cela ajoutera tous les jours fériés nationaux (fixes et islamiques). Les jours fériés déjà existants seront conservés.`}
        confirmText="Initialiser"
        cancelText="Annuler"
        variant="primary"
        icon={<Sparkles size={24} className="text-primary-600" />}
        onConfirm={handleInitialize}
        onCancel={() => setShowInitModal(false)}
        isLoading={initializing}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Supprimer le jour férié"
        message={`Êtes-vous sûr de vouloir supprimer "${holidayToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        icon={<Trash2 size={24} className="text-red-600" />}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setHolidayToDelete(null);
        }}
        isLoading={deleting}
      />

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingHoliday ? 'Modifier le jour férié' : 'Nouveau jour férié'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingHoliday(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom (français)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: Fête du Trône"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom (arabe) - optionnel
                </label>
                <input
                  type="text"
                  value={formData.nameAr || ''}
                  onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                  placeholder="عيد العرش"
                  dir="rtl"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    min={formData.startDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingHoliday(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {editingHoliday ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};
